import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button } from '@/components/shadcn/button';
import PadelEventHeader from '@/components/community/padel/PadelEventHeader';
import PadelStatistikContent from '@/components/community/padel/PadelStatistikContent';
import PadelStatistikDialogs from '@/components/community/padel/PadelStatistikDialogs';
import type { PadelStatistikTab } from '@/components/community/padel/PadelStatistikTabs';
import type { FlatPadelGame } from '@/components/community/padel/padelListUtils';
import type { PadelGameListItem } from '@/services/padelService';
import padelService from '@/services/padelService';
import { usePadelStatistik } from '@/pages/community/usePadelStatistik';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { playersLabel } from '@/components/community/padel/padelUiUtils';
import { impactLight } from '@/utils/nativeHaptics';
import showToast from '@/components/ui/Toast';
import mabarService from '@/services/mabarService';
import { downloadOrShareBlob } from '@/utils/downloadBlob';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import type { LeaderboardPlayerItem } from '@/components/ui/recap/LeaderboardRecapCard';

interface PendingDelete {
  game: PadelGameListItem;
  gameNumber: number;
}

export default function PadelStatistikPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientView = isCustomerAppPath(location.pathname);
  const data = usePadelStatistik(slug, isClientView);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as PadelStatistikTab | null;
  const validTabs: PadelStatistikTab[] = ['games', 'standings'];
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'games';
  const [tab, setTab] = useState<PadelStatistikTab>(initialTab);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam) && tabParam !== tab) {
      setTab(tabParam);
    }
  }, [tabParam, tab]);

  const handleTabChange = (newTab: PadelStatistikTab) => {
    setTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    setSearchParams(newParams, { replace: true });
  };

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lineupGame, setLineupGame] = useState<PadelGameListItem | null>(null);
  const [generatingNextRound, setGeneratingNextRound] = useState(false);
  const [downloadingStatistik, setDownloadingStatistik] = useState(false);

  const event = data.event;
  const isSparring = !!event?.config?.is_sparring;
  const teamAName = event?.config?.teams?.[0]?.name || 'Tim A';
  const teamBName = event?.config?.teams?.[1]?.name || 'Tim B';

  const showError = (_title: string, message: string) => showToast.error(message);
  const done = (_icon: string, _title: string, message: string) => {
    data.loadData();
    showToast.success(message);
  };

  if (data.loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!event || !slug) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-gray-600">{data.errorMessage || 'Event tidak ditemukan'}</p>
        <Button className="mt-4" onClick={() => navigate(isClientView ? '/event' : hostPaths.mabar.list)}>
          Kembali
        </Button>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const item = pendingDelete;
    setPendingDelete(null);
    try {
      await padelService.deleteGame(item.game.slug);
      await data.loadData();
      showToast.success(
        `Game ${item.gameNumber} (${playersLabel(item.game.sideAPlayers)} vs ${playersLabel(item.game.sideBPlayers)}) berhasil dihapus.`
      );
    } catch (err: unknown) {
      showError('Gagal menghapus', getApiErrorMessage(err, 'Gagal menghapus pertandingan'));
    }
  };

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await padelService.resetEventGames(slug);
      setResetOpen(false);
      await data.loadData();
      showToast.success('Semua data game berhasil direset.');
    } catch (err: unknown) {
      showError('Gagal mereset', getApiErrorMessage(err, 'Gagal mereset data game'));
    } finally {
      setResetting(false);
    }
  };

  const handleGenerateNextRound = async (sessionSlug: string) => {
    if (generatingNextRound) return;
    setGeneratingNextRound(true);
    impactLight();
    try {
      await padelService.generateNextRound(sessionSlug);
      await data.loadData();
      showToast.success('Jadwal ronde berikutnya berhasil dibuat.');
    } catch (err: unknown) {
      showError('Gagal generate ronde', getApiErrorMessage(err, 'Gagal membuat ronde baru'));
    } finally {
      setGeneratingNextRound(false);
    }
  };

  const handleShare = async () => {
    if (!slug) return;
    const shareUrl = `${window.location.origin}/event/${slug}/padel?tab=standings`;
    const title = `Klasemen Akhir - ${event?.name || 'Mabar Padel'}`;
    const text = `Lihat klasemen akhir pada event ${event?.name || ''}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast.success('Link klasemen berhasil disalin ke clipboard!');
    } catch {
      showToast.error('Gagal menyalin link');
    }
  };

  const handleDownloadStatistik = async () => {
    if (!slug || !event) return;
    if (!data.standings || data.standings.length === 0) {
      showToast.error('Belum ada data klasemen untuk diunduh');
      return;
    }

    setDownloadingStatistik(true);
    try {
      const leaderboardItems: LeaderboardPlayerItem[] = data.standings.slice(0, 5).map((p, idx) => ({
        position: p.position || idx + 1,
        playerName: p.playerName,
        avatarUrl: p.avatarUrl,
        matches: p.gamesPlayed,
        value: p.pointsScored,
        wins: p.wins,
        winRate: Math.round(p.winRate),
      }));

      const firstPlayer = leaderboardItems[0];
      const rawFirst = data.standings[0];
      const eventDateFormatted = formatDateDDMMYYYY(event.event_date);
      const location = event.lokasi_arena || event.address || '';
      const sportCategory = event.sport_category?.name || 'Padel';

      const blob = await mabarService.renderShareRecapV2(slug, {
        eventName: event.name,
        eventDateFormatted,
        stats: {
          goals: 0,
          assists: 0,
          matches: firstPlayer?.matches || 0,
          point: firstPlayer?.value || 0,
          point_loss: rawFirst?.pointsConceded || 0,
          wins: rawFirst?.wins || 0,
          win_rate: rawFirst?.winRate || 0,
          is_racket: true,
          rank: 1,
        },
        playerName: firstPlayer?.playerName || 'Pemain',
        playerAvatarUrl: firstPlayer?.avatarUrl,
        template: 'leaderboard_padel',
        sportCategory,
        location,
        standingsJson: JSON.stringify(leaderboardItems),
      });

      const filename = `klasemen-padel-${slug}.png`;
      const result = await downloadOrShareBlob(blob, filename, { title: event.name });
      if (result === 'saved') {
        showToast.success('Gambar klasemen berhasil diunduh/disimpan ke Galeri');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal mengunduh gambar klasemen';
      showToast.error(message);
    } finally {
      setDownloadingStatistik(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-4">
      <PadelEventHeader event={event} />
      <PadelStatistikContent
        tab={tab}
        onTabChange={handleTabChange}
        canEdit={data.canEdit}
        standings={data.standings}
        flatGames={data.flatGames}
        sessions={data.sessions}
        isSparring={isSparring}
        teamAName={teamAName}
        teamBName={teamBName}
        onTambahGame={() => {
          impactLight();
          navigate(
            isClientView ? clientPaths.event.padelTambahGame(slug) : hostPaths.mabar.padelTambahGame(slug)
          );
        }}
        onReset={() => setResetOpen(true)}
        onEditScore={(item: FlatPadelGame, gameNumber: number) =>
          navigate(
            isClientView
              ? clientPaths.event.padelEditSkor(slug, item.game.slug)
              : hostPaths.mabar.padelEditSkor(slug, item.game.slug),
            { state: { gameNumber } }
          )
        }
        onEditPlayer={setLineupGame}
        onDelete={(item, gameNumber) => setPendingDelete({ game: item.game, gameNumber })}
        onGenerateNextRound={handleGenerateNextRound}
        generatingNextRound={generatingNextRound}
        onShare={data.canEdit ? handleShare : undefined}
        onDownload={data.canEdit ? handleDownloadStatistik : undefined}
        downloading={downloadingStatistik}
      />

      <PadelStatistikDialogs
        participants={data.participants}
        isSparring={isSparring}
        teamAName={teamAName}
        teamBName={teamBName}
        lineupGame={lineupGame}
        pendingGameNumber={pendingDelete?.gameNumber ?? null}
        resetOpen={resetOpen}
        resetting={resetting}
        onCloseLineup={() => setLineupGame(null)}
        onCloseDelete={() => setPendingDelete(null)}
        onCloseReset={() => {
          if (!resetting) setResetOpen(false);
        }}
        onLineupSaved={() => {
          setLineupGame(null);
          done('mdi:account-switch-outline', 'Susunan pemain', 'Susunan pemain disimpan.');
        }}
        onConfirmDelete={() => void handleDelete()}
        onConfirmReset={() => void handleReset()}
        onError={showError}
      />
    </div>
  );
}
